/**
 * Viewport-aware positioning for floating menus — context menus, kebab
 * dropdowns, toolbar pickers.
 *
 * Menus default to opening downward (or, for right-click menus, down-and-right
 * of the cursor). These helpers flip a menu upward and clamp it horizontally
 * when there isn't room below/beside the anchor, so a menu opened near the
 * bottom edge of a scrollable list stays fully on-screen instead of being
 * clipped past the viewport.
 *
 * Both helpers measure the ALREADY-RENDERED menu element, so call them from a
 * `useLayoutEffect` (runs before paint → no visible jump). See any caller for
 * the pattern: render at a provisional position (optionally hidden), measure,
 * then set the corrected coords.
 */

// Keep at least this much space between the menu and the viewport edge.
const MARGIN = 8;

/**
 * Position a menu anchored to a raw click point (`clientX`/`clientY`), e.g. an
 * OS-style right-click context menu. Opens down-and-right of the point by
 * default; flips so its bottom/right edge sits at the point when it would
 * otherwise overflow the viewport.
 *
 * @param {HTMLElement} menuEl - the rendered menu (for measuring)
 * @param {number} x - click clientX
 * @param {number} y - click clientY
 * @param {number} [margin]
 * @returns {{ left: number, top: number }}
 */
export function positionMenuAtPoint(menuEl, x, y, margin = MARGIN) {
    const { width, height } = menuEl.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + width > window.innerWidth - margin) {
        left = Math.max(margin, x - width);
    }
    if (top + height > window.innerHeight - margin) {
        top = Math.max(margin, y - height);
    }
    return { left, top };
}

/**
 * Position a dropdown anchored to a trigger button. Opens directly below the
 * trigger by default; flips above it when there's more room up top than below.
 * Clamps horizontally so it never spills past either edge.
 *
 * @param {HTMLElement} menuEl - the rendered menu (for measuring)
 * @param {DOMRect} triggerRect - the trigger's getBoundingClientRect()
 * @param {{ gap?: number, margin?: number, align?: 'left'|'right' }} [opts]
 *   align: which menu edge lines up with the trigger ('left' = left edges,
 *   'right' = right edges). Horizontal is skipped when `align` is null.
 * @returns {{ left: number|null, top: number, openUp: boolean }}
 */
export function positionMenuByTrigger(menuEl, triggerRect, { gap = 4, margin = MARGIN, align = 'left' } = {}) {
    const { width, height } = menuEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openUp = spaceBelow < height + gap && spaceAbove > spaceBelow;
    let top = openUp ? triggerRect.top - height - gap : triggerRect.bottom + gap;
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));

    let left = null;
    if (align) {
        left = align === 'right' ? triggerRect.right - width : triggerRect.left;
        left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    }
    return { left, top, openUp };
}
